import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Card, Container, Row, Col, Table, Button, Badge, Spinner, Form, InputGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ReportingService } from '../../../service/reporting-service';

const RECALL_TYPES = [
    { key: '', label: 'Tutti i tipi' },
    { key: 'richiamo', label: 'Richiamo' },
    { key: 'trattenute', label: 'Trattenute' },
];

const PERIODS = [
    { key: 'all', label: 'Tutti' },
    { key: 'today', label: 'Oggi' },
    { key: 'overdue', label: 'Scaduti' },
    { key: 'this_week', label: 'Questa settimana' },
    { key: 'next_7', label: 'Prossimi 7 gg' },
    { key: 'upcoming', label: 'Futuri' },
];

const emptySummary = { total: 0, today: 0, overdue: 0, upcoming: 0, this_week: 0, next_7: 0 };

const toLocalDateKey = (dateStr) => {
    if (!dateStr) return null;
    const raw = String(dateStr).slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const formatDateKey = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const addDaysToKey = (dateKey, days) => {
    const [y, m, d] = dateKey.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + days);
    return formatDateKey(dt);
};

/** Sunday of the current week (matches backend weekday Mon=0 logic). */
const weekEndKeyFrom = (todayKey) => {
    const [y, m, d] = todayKey.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    const daysUntilSunday = (7 - dt.getDay()) % 7;
    dt.setDate(dt.getDate() + daysUntilSunday);
    return formatDateKey(dt);
};

/** Count buckets from actual data_per_richiamare values. */
const computeSummaryFromContacts = (contacts, reportedTotal, todayKey) => {
    const keys = (contacts || []).map((c) => toLocalDateKey(c.data_per_richiamare)).filter(Boolean);
    const weekEnd = weekEndKeyFrom(todayKey);
    const next7 = addDaysToKey(todayKey, 7);
    return {
        total: reportedTotal > 0 ? reportedTotal : keys.length,
        today: keys.filter((k) => k === todayKey).length,
        overdue: keys.filter((k) => k < todayKey).length,
        upcoming: keys.filter((k) => k > todayKey).length,
        this_week: keys.filter((k) => k >= todayKey && k <= weekEnd).length,
        next_7: keys.filter((k) => k >= todayKey && k <= next7).length,
    };
};

const isBrokenApiSummary = (summary, listTotal) => {
    if (!summary) return true;
    const total = Number(summary.total) || 0;
    const today = Number(summary.today) || 0;
    const overdue = Number(summary.overdue) || 0;
    const thisWeek = Number(summary.this_week) || 0;
    const upcoming = Number(summary.upcoming) || 0;
    if (listTotal > 0 && !total && !today && !overdue && !thisWeek && !upcoming) return true;
    // Same number in every box → period filter ignored / bad aggregate
    if (total > 0 && today === total && overdue === total && thisWeek === total) return true;
    if (total > 0 && today === overdue && overdue === thisWeek && today === total) return true;
    return false;
};

const Promemoria = () => {
    const { config } = useSelector((state) => state.auth);
    const canFilterAssignees = !!(config?.is_admin || config?.is_agency_boss || config?.is_agency_admin
        || config?.is_internal_backoffice_manager || config?.is_institute_backoffice_manager);

    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [total, setTotal] = useState(0);
    const [limit] = useState(25);
    const [summary, setSummary] = useState(emptySummary);
    const [filterOptions, setFilterOptions] = useState({ agents: [], agencies: [], campaigns: [] });

    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [sortField, setSortField] = useState('data_per_richiamare');
    const [sortDir, setSortDir] = useState('desc');
    const [period, setPeriod] = useState('today');
    const [assignedUser, setAssignedUser] = useState('');
    const [assignedAgency, setAssignedAgency] = useState('');
    const [campaign, setCampaign] = useState('');
    const [recallType, setRecallType] = useState('');

    const hasActiveFilters = !!(search || period !== 'today' || assignedUser || assignedAgency || campaign || recallType);

    const todayKey = useMemo(() => formatDateKey(new Date()), []);

    const buildDimensionParams = useCallback(() => {
        const params = {};
        if (search.trim()) params.search = search.trim();
        if (assignedUser) params.assigned_user = assignedUser;
        if (assignedAgency) params.assigned_agency = assignedAgency;
        if (campaign) params.campaign = campaign;
        if (recallType) params.recall_type = recallType;
        return params;
    }, [search, assignedUser, assignedAgency, campaign, recallType]);

    /** Load all matching contacts (no period) and count by date locally. */
    const loadSummary = useCallback(async () => {
        try {
            const service = new ReportingService();
            const base = {
                ...buildDimensionParams(),
                period: 'all',
                sort_field: 'data_per_richiamare',
                sort_dir: 'asc',
                limit: 100,
            };
            let pageNum = 1;
            let all = [];
            let reportedTotal = 0;
            let options = null;

            for (;;) {
                const r = await service.getPromemoriaContacts({ ...base, page: pageNum });
                if (pageNum === 1) {
                    options = r.filter_options || null;
                    // Prefer API summary only when buckets actually differ by date
                    if (r.summary && !isBrokenApiSummary(r.summary, Number(r.total) || 0)) {
                        setSummary({
                            total: Number(r.summary.total) || 0,
                            today: Number(r.summary.today) || 0,
                            overdue: Number(r.summary.overdue) || 0,
                            upcoming: Number(r.summary.upcoming) || 0,
                            this_week: Number(r.summary.this_week) || 0,
                            next_7: Number(r.summary.next_7) || 0,
                        });
                        if (options) setFilterOptions(options);
                        return;
                    }
                }
                reportedTotal = Number(r.total) || 0;
                const batch = r.contacts || [];
                all = all.concat(batch);
                if (!batch.length || all.length >= reportedTotal || pageNum >= 50) break;
                pageNum += 1;
            }

            if (options) setFilterOptions(options);
            setSummary(computeSummaryFromContacts(all, reportedTotal, todayKey));
        } catch (error) {
            console.error('Error loading promemoria summary', error);
        }
    }, [buildDimensionParams, todayKey]);

    const loadContacts = useCallback(async () => {
        setLoading(true);
        try {
            const service = new ReportingService();
            // Also send explicit date bounds so filtering works even if `period` is ignored
            const params = {
                page,
                limit,
                sort_field: sortField,
                sort_dir: sortDir,
                period,
                ...buildDimensionParams(),
            };

            if (period === 'today') {
                params.date_from = todayKey;
                params.date_to = todayKey;
            } else if (period === 'overdue') {
                params.date_to = addDaysToKey(todayKey, -1);
            } else if (period === 'upcoming') {
                params.date_from = addDaysToKey(todayKey, 1);
            } else if (period === 'this_week') {
                params.date_from = todayKey;
                params.date_to = weekEndKeyFrom(todayKey);
            } else if (period === 'next_7') {
                params.date_from = todayKey;
                params.date_to = addDaysToKey(todayKey, 7);
            }

            const response = await service.getPromemoriaContacts(params);
            setContacts(response.contacts || []);
            setTotal(Number(response.total) || 0);
            setTotalPages(response.total_pages || 0);
            if (response.filter_options) {
                setFilterOptions(response.filter_options);
            }
        } catch (error) {
            console.error('Error loading promemoria contacts', error);
        } finally {
            setLoading(false);
        }
    }, [page, limit, sortField, sortDir, period, buildDimensionParams, todayKey]);

    useEffect(() => {
        loadContacts();
    }, [loadContacts]);

    useEffect(() => {
        loadSummary();
    }, [loadSummary]);

    // Debounce search input → server search
    useEffect(() => {
        const t = setTimeout(() => {
            const next = searchInput.trim();
            if (next !== search) {
                setPage(1);
                setSearch(next);
            }
        }, 350);
        return () => clearTimeout(t);
    }, [searchInput, search]);

    const isToday = (dateStr) => toLocalDateKey(dateStr) === todayKey;
    const isOverdue = (dateStr) => {
        const key = toLocalDateKey(dateStr);
        return key !== null && key < todayKey;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            return new Date(dateStr).toLocaleDateString('it-IT');
        } catch (e) {
            return dateStr;
        }
    };

    const clearFilters = () => {
        setSearchInput('');
        setSearch('');
        setPeriod('today');
        setAssignedUser('');
        setAssignedAgency('');
        setCampaign('');
        setRecallType('');
        setPage(1);
    };

    const setPeriodAndReset = (key) => {
        setPeriod(key);
        setPage(1);
    };

    const SortableHeader = ({ label, field, className }) => (
        <th
            className={className}
            style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
            onClick={() => {
                if (sortField === field) {
                    setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                } else {
                    setSortField(field);
                    setSortDir('asc');
                }
                setPage(1);
            }}
        >
            {label}
            {sortField === field && (
                <i className={`ri-arrow-${sortDir === 'asc' ? 'up' : 'down'}-s-line ml-1`} style={{ fontSize: '12px' }} />
            )}
        </th>
    );

    const SummaryCard = ({ label, value, active, onClick, accent }) => (
        <button
            type="button"
            onClick={onClick}
            className="text-left w-100"
            style={{
                background: active ? (accent || '#f8f9fa') : '#fff',
                borderRadius: '10px',
                padding: '12px 14px',
                border: active ? '2px solid #007bff' : '1px solid #dee2e6',
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            }}
        >
            <div className="text-muted small mb-1">{label}</div>
            <div className="font-weight-bold text-dark" style={{ fontSize: '1.5rem', lineHeight: 1.1 }}>
                {value ?? 0}
            </div>
        </button>
    );


    return (
        <Container fluid>
            <Row className="mb-3">
                <Col className="d-flex flex-row justify-content-between align-items-center">
                    <div>
                        <h2 className="font-weight-bold mb-0">Promemoria per Richiamare</h2>
                        <small className="text-muted">Contatti con richiamo concordato</small>
                    </div>
                    <Button
                        variant="primary"
                        onClick={() => { setPage(1); loadContacts(); }}
                        className="px-4"
                        style={{ borderRadius: '8px' }}
                    >
                        <i className="ri-refresh-line mr-1" /> Aggiorna
                    </Button>
                </Col>
            </Row>

            <Row className="mb-3">
                <Col xs={6} md={3} className="mb-2">
                    <SummaryCard
                        label="Oggi"
                        value={summary.today}
                        active={period === 'today'}
                        onClick={() => setPeriodAndReset(period === 'today' ? 'all' : 'today')}
                        accent="#fee2e2"
                    />
                </Col>
                <Col xs={6} md={3} className="mb-2">
                    <SummaryCard
                        label="Scaduti"
                        value={summary.overdue}
                        active={period === 'overdue'}
                        onClick={() => setPeriodAndReset(period === 'overdue' ? 'all' : 'overdue')}
                        accent="#ffedd5"
                    />
                </Col>
                <Col xs={6} md={3} className="mb-2">
                    <SummaryCard
                        label="Questa settimana"
                        value={summary.this_week}
                        active={period === 'this_week'}
                        onClick={() => setPeriodAndReset(period === 'this_week' ? 'all' : 'this_week')}
                        accent="#e9ecef"
                    />
                </Col>
                <Col xs={6} md={3} className="mb-2">
                    <SummaryCard
                        label="Totale"
                        value={summary.total}
                        active={period === 'all'}
                        onClick={() => setPeriodAndReset('all')}
                        accent="#f8f9fa"
                    />
                </Col>
            </Row>

            <Row>
                <Col md={12}>
                    <Card className="shadow-sm" style={{ borderRadius: '12px' }}>
                        <Card.Header className="bg-white border-bottom py-2" style={{ borderRadius: '12px 12px 0 0' }}>
                            <div className="d-flex flex-nowrap align-items-center mb-0" style={{ overflowX: 'auto' }}>
                                <div className="mr-2 flex-shrink-0" style={{ width: '150px' }}>
                                    <Form.Control
                                        as="select"
                                        size="sm"
                                        value={recallType}
                                        onChange={(e) => { setRecallType(e.target.value); setPage(1); }}
                                    >
                                        {RECALL_TYPES.map((t) => (
                                            <option key={t.key || 'all'} value={t.key}>{t.label}</option>
                                        ))}
                                    </Form.Control>
                                </div>
                                <div className="mr-2 flex-shrink-0" style={{ width: '150px' }}>
                                    <Form.Control
                                        as="select"
                                        size="sm"
                                        value={period}
                                        onChange={(e) => setPeriodAndReset(e.target.value)}
                                    >
                                        {PERIODS.map((p) => (
                                            <option key={p.key} value={p.key}>{p.label}</option>
                                        ))}
                                    </Form.Control>
                                </div>
                                <div className="mr-2 flex-shrink-0" style={{ width: '180px' }}>
                                    <Form.Control
                                        size="sm"
                                        placeholder="Cerca..."
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                    />
                                </div>
                                {canFilterAssignees && (
                                    <>
                                        <div className="mr-2 flex-shrink-0" style={{ width: '150px' }}>
                                            <Form.Control
                                                as="select"
                                                size="sm"
                                                value={assignedUser}
                                                onChange={(e) => { setAssignedUser(e.target.value); setPage(1); }}
                                            >
                                                <option value="">Tutti gli agenti</option>
                                                <option value="null">Non assegnati</option>
                                                {(filterOptions.agents || []).map((a) => (
                                                    <option key={a.email} value={a.email}>{a.name || a.email}</option>
                                                ))}
                                            </Form.Control>
                                        </div>
                                        <div className="mr-2 flex-shrink-0" style={{ width: '150px' }}>
                                            <Form.Control
                                                as="select"
                                                size="sm"
                                                value={assignedAgency}
                                                onChange={(e) => { setAssignedAgency(e.target.value); setPage(1); }}
                                            >
                                                <option value="">Tutte le agenzie</option>
                                                <option value="null">Senza agenzia</option>
                                                {(filterOptions.agencies || []).map((a) => (
                                                    <option key={a.id} value={a.id}>{a.name}</option>
                                                ))}
                                            </Form.Control>
                                        </div>
                                    </>
                                )}
                                <div className="mr-2 flex-shrink-0" style={{ width: '150px' }}>
                                    <Form.Control
                                        as="select"
                                        size="sm"
                                        value={campaign}
                                        onChange={(e) => { setCampaign(e.target.value); setPage(1); }}
                                    >
                                        <option value="">Tutte le campagne</option>
                                        <option value="null">Senza campagna</option>
                                        {(filterOptions.campaigns || []).map((c) => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </Form.Control>
                                </div>
                                {hasActiveFilters && (
                                    <div className="flex-shrink-0">
                                        <Button size="sm" variant="link" className="text-muted px-1" onClick={clearFilters}>
                                            Azzera
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </Card.Header>

                        <Card.Body className="p-0">
                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2 text-muted small">Caricamento contatti...</p>
                                </div>
                            ) : contacts.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="ri-checkbox-circle-line" style={{ fontSize: '48px', color: '#10B981' }} />
                                    <p className="text-muted mt-2">
                                        {hasActiveFilters ? 'Nessun risultato con i filtri selezionati' : 'Nessun contatto da richiamare'}
                                    </p>
                                    {hasActiveFilters && (
                                        <Button size="sm" variant="outline-secondary" onClick={clearFilters}>
                                            Azzera filtri
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table hover size="sm" className="mb-0">
                                        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                                            <tr>
                                                <SortableHeader label="Nome Contatto" field="name" />
                                                <SortableHeader label="Telefono" field="telefone" />
                                                <th>Email</th>
                                                <SortableHeader label="Data Richiamo" field="data_per_richiamare" className="text-center" />
                                                <th>Tipo</th>
                                                {canFilterAssignees && (
                                                    <SortableHeader label="Agente" field="assigned_user" />
                                                )}
                                                {canFilterAssignees && (
                                                    <SortableHeader label="Agenzia" field="assigned_agency" />
                                                )}
                                                <SortableHeader label="Campagna" field="campaign" />
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {contacts.map((contact, idx) => (
                                                <tr
                                                    key={contact.id}
                                                    style={{
                                                        backgroundColor: isToday(contact.data_per_richiamare)
                                                            ? 'rgba(239, 68, 68, 0.06)'
                                                            : (idx % 2 === 0 ? 'transparent' : '#f9fafb'),
                                                    }}
                                                >
                                                    <td style={{ whiteSpace: 'nowrap' }}>
                                                        <strong className="small">{contact.name} {contact.surname}</strong>
                                                        {contact.recall_type !== 'trattenute' && contact.recall_kind === 'rinnovo' && (
                                                            <Badge variant="info" className="ml-1" style={{ fontSize: '10px' }}>Rinnovo</Badge>
                                                        )}
                                                        {contact.codice_fiscale ? (
                                                            <div className="text-muted" style={{ fontSize: '11px' }}>{contact.codice_fiscale}</div>
                                                        ) : null}
                                                    </td>
                                                    <td className="small" style={{ whiteSpace: 'nowrap' }}>
                                                        {contact.telefone || '-'}
                                                    </td>
                                                    <td className="small" style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {contact.email || '-'}
                                                    </td>
                                                    <td className="text-center">
                                                        {contact.data_per_richiamare ? (
                                                            isToday(contact.data_per_richiamare) ? (
                                                                <Badge variant="danger" className="px-2 py-1" style={{ fontSize: '11px' }}>
                                                                    {formatDate(contact.data_per_richiamare)}
                                                                </Badge>
                                                            ) : isOverdue(contact.data_per_richiamare) ? (
                                                                <span className="small text-muted">{formatDate(contact.data_per_richiamare)}</span>
                                                            ) : (
                                                                <span className="small">{formatDate(contact.data_per_richiamare)}</span>
                                                            )
                                                        ) : (
                                                            <span className="small">-</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <Badge
                                                            variant={contact.recall_type === 'trattenute' ? 'warning' : 'secondary'}
                                                            style={{ fontSize: '10px' }}
                                                        >
                                                            {contact.recall_type === 'trattenute' ? 'Trattenute' : 'Richiamo'}
                                                        </Badge>
                                                    </td>
                                                    {canFilterAssignees && (
                                                        <td className="small">{contact.assigned_user || <span className="text-muted">—</span>}</td>
                                                    )}
                                                    {canFilterAssignees && (
                                                        <td className="small">{contact.assigned_agency || <span className="text-muted">—</span>}</td>
                                                    )}
                                                    <td className="small">{contact.campaign || <span className="text-muted">—</span>}</td>
                                                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                                                        <Link
                                                            to={`/contact-view/${contact.id}`}
                                                            className="btn btn-sm btn-outline-primary"
                                                            style={{ borderRadius: '6px', padding: '2px 10px', fontSize: '12px' }}
                                                            title="Apri contatto"
                                                        >
                                                            <i className="ri-eye-line" />
                                                        </Link>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Card.Body>

                        <Card.Footer
                            className="bg-white border-top d-flex justify-content-between align-items-center py-2"
                            style={{ borderRadius: '0 0 12px 12px' }}
                        >
                            <small className="text-muted">
                                {totalPages > 0
                                    ? `Pagina ${page} di ${totalPages} — ${total} risultati`
                                    : `${total} risultati`}
                                {hasActiveFilters ? ' (filtrati)' : ''}
                            </small>
                            {totalPages > 1 && (
                                <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        disabled={page <= 1}
                                        onClick={() => setPage((p) => p - 1)}
                                        style={{ padding: '2px 8px', fontSize: '12px', borderRadius: '6px' }}
                                    >
                                        <i className="ri-arrow-left-s-line" />
                                    </Button>
                                    <small className="text-muted mx-1">{page}/{totalPages}</small>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        disabled={page >= totalPages}
                                        onClick={() => setPage((p) => p + 1)}
                                        style={{ padding: '2px 8px', fontSize: '12px', borderRadius: '6px' }}
                                    >
                                        <i className="ri-arrow-right-s-line" />
                                    </Button>
                                </div>
                            )}
                        </Card.Footer>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Promemoria;
